import type {
	AvailableIntegration,
	EnrichedIntegration,
	IntegrationConnector,
} from './types'

/**
 * Join connected connectors against the available-integration catalog, keyed on
 * `connector.definition_id === availableIntegration.id`. Pure, in-memory, over a
 * handful of rows — cheap enough to run on the main thread inside a `useMemo`.
 *
 * Skip-missing guard: a connector whose `definition_id` has NO catalog entry at
 * all is `console.warn`ed and dropped (the page shows fewer cards, never an
 * error). This is a data-integrity guard — every connector's definition id must
 * live in the catalog it's served alongside.
 *
 * Note: the caller must pass the UNFILTERED catalog. `visible` is an add-catalog
 * flag, not a connected-list filter — a connector on a `visible:false` definition
 * (e.g. "Liongard Test") must still render in the connected list.
 */
export function enrichIntegrations(
	available: Array<AvailableIntegration>,
	list: Array<IntegrationConnector>,
): Array<EnrichedIntegration> {
	const catalog = new Map(available.map((entry) => [entry.id, entry]))
	const enriched: Array<EnrichedIntegration> = []

	for (const { connector, state } of list) {
		const definition = catalog.get(connector.definition_id)

		if (!definition) {
			console.warn(
				`No available integration found for definition_id: ${connector.definition_id}`,
			)
			continue
		}

		enriched.push({
			id: connector.id,
			name: connector.name,
			definition_id: connector.definition_id,
			definition_name: definition.name,
			vendor: definition.vendor,
			product: definition.product,
			event_types: definition.event_types,
			capabilities: definition.capabilities,
			category: definition.category,
			default: connector.default,
			state,
		})
	}

	return enriched
}
